/**
 * src/api/control.js
 *
 * All API calls related to the Home tab's control panel.
 * Backed by gaussiq-intelligence's /api/control/* routes. Translates between
 * the backend's snake_case envelope and the camelCase shape components use
 * (see src/api/knowledge.js for the same pattern). The contents of the
 * `hero`/`meta`/`sections`/etc. blobs are opaque pre-shaped UI
 * payloads the backend passes through verbatim, so those stay untranslated.
 */

import { authedJson } from './http'

const apiGet = path => authedJson('GET', path)

// insight_job_run_id doubles as the Home card's `id` — it's what
// fetchControlInsight needs, and there's exactly one current run per job.
function fromApiCard(card) {
  return {
    id: card.insight_job_run_id,
    rank: card.rank,
    accent: card.accent,
    title: card.title,
    subtitle: card.subtitle,
    // A freshly-selected job that hasn't run yet has no hero content —
    // its run row is a bare 'pending' placeholder (see gaussiq-intelligence's
    // control/summary.py). The Home tab renders those as a placeholder card.
    pending: card.status === 'pending',
    hero: card.hero,
  }
}

function fromApiSummary(data) {
  return {
    efficiencyGain: data.efficiency_gain,
    cards: (data.cards ?? []).map(fromApiCard),
  }
}

function fromApiInsight(data) {
  return {
    insightJobRunId: data.insight_job_run_id,
    insightJobId: data.insight_job_id,
    title: data.title,
    subtitle: data.subtitle,
    footerCaption: data.footer_caption,
    updatedLabel: data.updated_label,
    runBlocks: data.run_blocks ?? [],
    opportunities: data.opportunities ?? [],
  }
}

function fromApiRun(run) {
  return {
    id: run.insight_job_run_id,
    date: run.created_at,
  }
}

/**
 * Fetch everything needed to render the Home tab's efficiency-gain panel
 * and insight cards (each card's `hero` is a generic block array — see
 * BlockRenderer). The Customer Outcome Performance panel is fed separately
 * by fetchOutcomeMetrics (src/api/metrics.js).
 * GET /control/summary
 * @returns {Promise<{
 *   efficiencyGain: object,
 *   cards: Array<{id, rank, accent, title, subtitle, pending: boolean, hero: object[]}>
 * }>}
 */
export async function fetchControlSummary() {
  const data = await apiGet('/control/summary')
  return fromApiSummary(data)
}

/**
 * Fetch everything needed to render the Insight Detail modal for one
 * insight job run: a run-level `runBlocks` list shown once across
 * opportunities, plus every ranked opportunity, each with its own `meta`
 * strip and a `sections` array — the modal's tabs, each `{label, blocks}`
 * where `blocks` is a typed-block body (see BlockRenderer). The tabs are
 * grouped server-side, so the frontend renders them as given. The left-rail
 * opportunity switch and the section tabs are both client-side only against
 * this one payload — no per-tab refetch.
 * GET /control/insight/{insight_job_run_id}
 * @param {number} insightJobRunId — matches a summary card's `id`
 * @returns {Promise<{
 *   insightJobRunId: number, insightJobId: number, title: string, subtitle: string,
 *   footerCaption: string, updatedLabel: string, runBlocks: object[],
 *   opportunities: Array<{id, rank, label, executions, contacts, title, tags: string[],
 *     meta: Array<{label, value, color_key}>, sections: Array<{label: string, blocks: object[]}>}>
 * }>}
 */
export async function fetchControlInsight(insightJobRunId) {
  const data = await apiGet(`/control/insight/${insightJobRunId}`)
  return fromApiInsight(data)
}

/**
 * Fetch every completed run of one insight job, newest first — powers the
 * Insight Detail modal's run-history date selector.
 * GET /control/insight-jobs/{insight_job_id}/runs
 * @param {number} insightJobId — matches an insight payload's `insightJobId`
 * @returns {Promise<Array<{id: number, date: string}>>}
 */
export async function fetchInsightJobRuns(insightJobId) {
  const data = await apiGet(`/control/insight-jobs/${insightJobId}/runs`)
  return (data.runs ?? []).map(fromApiRun)
}
